/**
 * "The Hub lost its connection. Tap to reload." (packet B815)
 *
 * Shows only when an iPhone has closed the Hub's database under a page and
 * the automatic reload was not safe yet, or was already spent inside its ten
 * minute window (lib/firestoreClosedRecovery.ts says when and why). The whole
 * bar is one button and a tap reloads.
 *
 * Non-blocking: a small bar at the top, clear of the bottom band where the
 * upload banner, the update pill and Back sit, because an upload in flight is
 * the most likely reason this bar is up at all. Only the button takes taps;
 * the page underneath stays usable around it.
 *
 * Mounted in main.tsx beside the app rather than inside it, behind its own
 * OptionalFeatureBoundary, so it still shows when the dead database has taken
 * the app down to its error screen, and so a fault in it can never take the
 * app down. It needs no router and no provider: the colours are the global
 * theme tokens on <html>.
 */
import { useSyncExternalStore } from 'react';
import {
  getFirestoreRecoverySnapshot,
  reloadAfterFirestoreClosed,
  subscribeFirestoreRecovery,
} from '../lib/firestoreRecovery';
import { FIRESTORE_RECOVERY_BAR_TEXT } from '../lib/firestoreClosedRecovery';

export default function FirestoreRecoveryBar() {
  const { barVisible } = useSyncExternalStore(
    subscribeFirestoreRecovery,
    getFirestoreRecoverySnapshot,
    getFirestoreRecoverySnapshot,
  );
  if (!barVisible) return null;
  return (
    <div
      className="no-print"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
        left: 0,
        right: 0,
        // Above the nav drawer and the update pill (1300), below the forced
        // password change sheet (2000), which must stay on top of everything.
        zIndex: 1310,
        display: 'flex',
        justifyContent: 'center',
        padding: '0 16px',
        boxSizing: 'border-box',
        pointerEvents: 'none',
      }}
    >
      <button
        type="button"
        data-firestore-recovery-bar=""
        onClick={() => reloadAfterFirestoreClosed()}
        style={{
          pointerEvents: 'auto',
          minHeight: 'var(--tap-target, 44px)',
          maxWidth: 480,
          width: '100%',
          padding: '8px 16px',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-pill)',
          background: 'var(--paper)',
          color: 'var(--ink)',
          boxShadow: '0 10px 26px rgba(35,28,20,0.22)',
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1.3,
          textAlign: 'center',
          cursor: 'pointer',
        }}
      >
        {FIRESTORE_RECOVERY_BAR_TEXT}
      </button>
    </div>
  );
}
